<script setup lang="ts">
import { Search, X } from 'lucide-vue-next'

/**
 * Search dialog component
 * Opens as a modal overlay with search input and results
 * Fetches prebuilt search index on first open, then filters locally
 */

interface SearchItem {
  id: string
  title: string
  description: string
  category: string
  href: string
  keywords?: string[]
}

interface SearchIndex {
  generated_at: string
  version: number
  count: number
  items: SearchItem[]
}

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const searchQuery = ref('')
const searchInput = ref<HTMLInputElement | null>(null)
const isLoadingIndex = ref(false)
const indexLoadError = ref(false)

// Search index - fetched once on first open
const searchIndex = ref<SearchIndex | null>(null)

// Filtered search results
const searchResults = ref<SearchItem[]>([])

function closeDialog() {
  emit('update:open', false)
  searchQuery.value = ''
  searchResults.value = []
}

function clearSearch() {
  searchQuery.value = ''
  searchResults.value = []
  searchInput.value?.focus()
}

// Fetch index when dialog opens (once)
watch(() => props.open, async (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      searchInput.value?.focus()
    })

    // Fetch index on first open
    if (searchIndex.value === null && !isLoadingIndex.value) {
      isLoadingIndex.value = true
      indexLoadError.value = false

      try {
        const data = await $fetch<SearchIndex>('/api/search')
        searchIndex.value = data
      } catch (error) {
        console.error('Failed to load search index:', error)
        indexLoadError.value = true
        searchIndex.value = { generated_at: '', version: 1, count: 0, items: [] }
      } finally {
        isLoadingIndex.value = false
      }
    }
  }
})

// Handle escape key
function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    closeDialog()
  }
}

/**
 * Score and filter search results locally
 * Scoring rules:
 * - title includes token: +10
 * - keyword includes token or token includes keyword: +6
 * - description includes token: +3
 * - title starts with query: +4 bonus
 */
function filterResults(query: string): SearchItem[] {
  if (!searchIndex.value?.items || !query.trim()) {
    return []
  }

  const normalizedQuery = query.toLowerCase().trim()
  const tokens = normalizedQuery.split(/\s+/).filter(t => t.length > 0)

  if (tokens.length === 0) {
    return []
  }

  const scored: Array<{ item: SearchItem; score: number }> = []

  for (const item of searchIndex.value.items) {
    let score = 0
    const titleLower = item.title.toLowerCase()
    const descLower = item.description.toLowerCase()
    const keywordsLower = (item.keywords || []).map(k => k.toLowerCase())

    for (const token of tokens) {
      // Title match: +10
      if (titleLower.includes(token)) {
        score += 10
      }

      // Keyword match: +6 (bidirectional - token in keyword OR keyword in token)
      for (const kw of keywordsLower) {
        if (kw.includes(token) || token.includes(kw)) {
          score += 6
          break // Only count once per token
        }
      }

      // Description match: +3
      if (descLower.includes(token)) {
        score += 3
      }
    }

    // Bonus: title starts with full query: +4
    if (titleLower.startsWith(normalizedQuery)) {
      score += 4
    }

    if (score > 0) {
      scored.push({ item, score })
    }
  }

  // Sort by score (desc), then by title (asc)
  scored.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score
    }
    return a.item.title.localeCompare(b.item.title)
  })

  // Limit to 15 results
  return scored.slice(0, 15).map(s => s.item)
}

/**
 * Get Tailwind classes for category tags based on category name
 */
function getCategoryClasses(category: string): string {
  const categoryColors: Record<string, string> = {
    // Service categories
    'Security': 'bg-red-100 text-red-700',
    'Industry Security': 'bg-red-100 text-red-700',
    'Internet & Data': 'bg-blue-100 text-blue-700',
    'Voice & Video': 'bg-purple-100 text-purple-700',
    'Cloud Services': 'bg-sky-100 text-sky-700',
    'Managed IT': 'bg-teal-100 text-teal-700',
    'Web Solutions': 'bg-indigo-100 text-indigo-700',
    'Radio Services': 'bg-orange-100 text-orange-700',
    'Project Services': 'bg-amber-100 text-amber-700',
    // Content categories
    'About Us': 'bg-slate-100 text-slate-700',
    'Our Projects': 'bg-slate-100 text-slate-700',
    'Industries': 'bg-emerald-100 text-emerald-700',
    'Help Centre': 'bg-cyan-100 text-cyan-700',
    'Pages': 'bg-neutral-100 text-neutral-600',
    // Blog categories
    'Case Studies': 'bg-violet-100 text-violet-700',
    'News': 'bg-rose-100 text-rose-700',
    'Tips & Guides': 'bg-lime-100 text-lime-700',
    'Blog': 'bg-fuchsia-100 text-fuchsia-700',
  }
  return categoryColors[category] || 'bg-primary-100 text-primary-700'
}

// Debounced local filtering
const debouncedSearch = useDebounceFn((query: string) => {
  if (!query.trim()) {
    searchResults.value = []
    return
  }

  // Don't search if index isn't loaded yet
  if (!searchIndex.value) {
    searchResults.value = []
    return
  }

  searchResults.value = filterResults(query)
}, 150) // Shorter debounce since filtering is local

watch(searchQuery, (query) => {
  debouncedSearch(query)
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-50 overflow-y-auto"
        @keydown="handleKeydown"
      >
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-dark/60 backdrop-blur-sm"
          @click="closeDialog"
        ></div>

        <!-- Dialog -->
        <div class="relative flex min-h-full items-start justify-center p-4 pt-[15vh]">
          <div
            class="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Search"
          >
            <!-- Search input -->
            <div class="flex items-center border-b border-neutral-200">
              <Search class="ml-4 h-5 w-5 text-neutral-400" aria-hidden="true" />
              <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                placeholder="Search services, help articles..."
                class="flex-1 border-0 bg-transparent px-4 py-4 text-lg text-dark placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                :disabled="isLoadingIndex"
              />
              <button
                v-if="searchQuery"
                type="button"
                class="p-2 text-neutral-400 hover:text-neutral-600"
                @click="clearSearch"
              >
                <X class="h-5 w-5" />
                <span class="sr-only">Clear search</span>
              </button>
              <button
                type="button"
                class="mr-2 rounded-lg px-3 py-1.5 text-sm font-medium text-neutral-500 hover:bg-neutral-100"
                @click="closeDialog"
              >
                Close
              </button>
            </div>

            <!-- Results -->
            <div class="max-h-[60vh] overflow-y-auto">
              <!-- Loading index state -->
              <div v-if="isLoadingIndex" class="p-8 text-center">
                <div class="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                <p class="mt-2 text-sm text-neutral-500">Loading search...</p>
              </div>

              <!-- Index load error -->
              <div v-else-if="indexLoadError && !searchIndex?.items.length" class="p-8 text-center">
                <p class="text-neutral-500">Search is temporarily unavailable. Please try again later.</p>
              </div>

              <!-- Empty state (has query but no results) -->
              <div v-else-if="searchQuery && searchResults.length === 0" class="p-8 text-center">
                <p class="text-neutral-500">No results found for "{{ searchQuery }}"</p>
                <p class="mt-2 text-sm text-neutral-400">Try different keywords or browse the quick links below</p>
              </div>

              <!-- Results list -->
              <div v-else-if="searchResults.length > 0" class="divide-y divide-neutral-100">
                <NuxtLink
                  v-for="result in searchResults"
                  :key="result.id"
                  :to="result.href"
                  class="block px-4 py-4 hover:bg-primary-50"
                  @click="closeDialog"
                >
                  <p class="font-medium text-primary-700">{{ result.title }}</p>
                  <p v-if="result.description" class="mt-1 text-sm text-neutral-500 line-clamp-2">{{ result.description }}</p>
                  <span
                    class="mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="getCategoryClasses(result.category)"
                  >
                    {{ result.category }}
                  </span>
                </NuxtLink>
              </div>

              <!-- Initial state / suggestions -->
              <div v-else class="p-6">
                <p class="text-sm font-medium text-neutral-500">Quick Links</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <NuxtLink
                    to="/internet-data/business-nbn"
                    class="rounded-full bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-primary-100 hover:text-primary-700"
                    @click="closeDialog"
                  >
                    Business NBN
                  </NuxtLink>
                  <NuxtLink
                    to="/security-services/cctv"
                    class="rounded-full bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-primary-100 hover:text-primary-700"
                    @click="closeDialog"
                  >
                    CCTV Systems
                  </NuxtLink>
                  <NuxtLink
                    to="/voice-video/voip-phone"
                    class="rounded-full bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-primary-100 hover:text-primary-700"
                    @click="closeDialog"
                  >
                    VoIP Phone
                  </NuxtLink>
                  <NuxtLink
                    to="/managed-it-services/au-service-desk"
                    class="rounded-full bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-primary-100 hover:text-primary-700"
                    @click="closeDialog"
                  >
                    IT Support
                  </NuxtLink>
                  <NuxtLink
                    to="/contact"
                    class="rounded-full bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-primary-100 hover:text-primary-700"
                    @click="closeDialog"
                  >
                    Contact Us
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
